import React, { useState, useEffect } from "react";
import {
  Grid,
  Typography,
  Select,
  MenuItem,
  TextField,
  Checkbox,
  IconButton,
  Tooltip,
  ListSubheader,
  FormControl,
  InputLabel,
  Box,
  Stack,
  Autocomplete,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import Divider from "@mui/material/Divider";
import typography from "../../cssUtils/typography";
import { CallingCodeSelect } from "../../common/country-code-select";
import { useAuth } from "../../hooks/AuthProvider";
import { FORMS_FORMS } from "../../config/urls";

// Contact (audience) fields a WhatsApp Form button can prefill from; read by
// nodebot _form_prefill_data.
const CONTACT_FIELDS = ["name", "phone", "email", "company", "city", "state", "language", "age"];

const ButtonComponent = ({
  templateType,
  allButtons,
  setAllButtons,
  setIsButtonsValid,
  setFooter,
  body,
}) => {
  // Body variables ({{1}}, {{2}} ...): a prefill can take the recipient's value of one.
  const bodyVariables = [
    ...new Set((String(body || "").match(/\{\{\d+\}\}/g) || []).map((m) => m.slice(2, -2))),
  ].sort((a, b) => a - b);
  const [selectedOption, setSelectedOption] = useState("");
  const [tAndCChecked, setTAndCChecked] = useState(false);
  const [tollFreeNumber, setTollFreeNumber] = useState(false);

  const tenant = localStorage.getItem("tenant");
  const { getValidToken } = useAuth();

  // WhatsApp Form button: published forms to pick from, and the picked
  // form's prefillable fields keyed by form id.
  const [publishedForms, setPublishedForms] = useState([]);
  const [formFields, setFormFields] = useState({});
  // Screen whose prefill fields are shown, keyed by button id.
  const [prefillScreen, setPrefillScreen] = useState({});

  // Track count of each button type (max: marketing-opt-out:1, custom:1, visit-website:2, call-phone-number:1, 'copy-code':1, whatsapp-form:1)
  const [buttonCounts, setButtonCounts] = useState({
    "marketing-opt-out": 0,
    custom: 0,
    "visit-website": 0,
    "call-phone-number": 0,
    "copy-code": 0,
    "whatsapp-form": 0,
  });

  const formApi = async (url) => {
    const token = await getValidToken();
    const res = await fetch(url, {
      headers: { Authorization: `Bearer ${token}`, "X-Tenant-Id": tenant },
    });
    if (!res.ok) throw new Error("Failed to load forms");
    return res.json();
  };

  const hasFormButton = allButtons.some((b) => b.type === "whatsapp-form");
  useEffect(() => {
    if (!hasFormButton || publishedForms.length > 0) return;
    formApi(`${FORMS_FORMS}?status=PUBLISHED&limit=1000`)
      .then((data) => setPublishedForms(data.results || data || []))
      .catch(() => setPublishedForms([]));
  }, [hasFormButton]);

  // A form's prefillable fields: fields on any screen the builder declared a
  // pf_<name> value for in the first screen's data (backend forms/builder.py _with_prefill).
  // Listed in screen and field order: the stored JSON (jsonb) reorders object keys,
  // so the order of the pf_ keys themselves means nothing.
  const loadFormFields = async (formId) => {
    if (!formId || formFields[formId]) return;
    const form = await formApi(`${FORMS_FORMS}${formId}/`);
    const screens = form?.form_json?.screens || [];
    const declared = screens[0]?.data || {};
    const seen = new Set();
    const fields = screens.flatMap((s, i) => {
      const screen = { id: s.id || `${i}`, title: `${i + 1}. ${s.title || `Screen ${i + 1}`}` };
      return (s.layout?.children?.[0]?.children || [])
        .filter((el) => el.name && `pf_${el.name}` in declared && !seen.has(el.name))
        .map((el) => {
          seen.add(el.name);
          return { name: el.name, label: el.label || el.name, screen };
        });
    });
    setFormFields((prev) => ({ ...prev, [formId]: fields }));
  };

  // Load the fields of a form picked earlier (template reopened / edited).
  useEffect(() => {
    allButtons
      .filter((b) => b.type === "whatsapp-form" && b.data?.formId)
      .forEach((b) => loadFormFields(b.data.formId).catch(() => {}));
  }, [allButtons]);

  // Reset function for button-specific states
  const resetButtonStates = (type) => {
    switch (type) {
      case "marketing-opt-out":
        setTAndCChecked(false);
        setFooter("");
        break;
      // Add more cases for other button types
      default:
        break;
    }
  };

  // Check if total button limit (10) is reached
  const isButtonLimitReached = () => {
    return allButtons.length >= 10;
  };

  // Verify if more buttons of specific type can be added
  const canAddButtonType = (type) => {
    const limits = {
      "marketing-opt-out": 1,
      custom: 10,
      "visit-website": 2,
      "call-phone-number": 1,
      "whatsapp-form": 1,
    };
    return buttonCounts[type] < limits[type];
  };

  const hasDynamicPlaceholder = (url) => {
    return url.includes("{{1}}");
  };

  // Meta: quick reply and call-to-action buttons must form two groups, not be mixed
  const isQuickReply = (button) =>
    button.type === "marketing-opt-out" || button.type === "custom";
  const buttonsMixed =
    allButtons
      .slice(1)
      .filter((button, i) => isQuickReply(button) !== isQuickReply(allButtons[i]))
      .length > 1;

  useEffect(() => {
    const marketingOptOutExists = allButtons.some(
      (button) => button.type === "marketing-opt-out"
    );
    // A WhatsApp Form button needs a label and a published form.
    const formButtonsValid = allButtons
      .filter((button) => button.type === "whatsapp-form")
      .every((button) => button.data.buttonText?.trim() && button.data.formId);
    if (buttonsMixed) {
      setIsButtonsValid(false);
    } else if (marketingOptOutExists) {
      setIsButtonsValid(tAndCChecked && formButtonsValid);
    } else {
      setIsButtonsValid(formButtonsValid);
    }
  }, [allButtons, tAndCChecked, setIsButtonsValid, buttonsMixed]);

  // Enhanced option change handler to support multiple buttons
  const handleOptionChange = (event) => {
    const option = event.target.value;
    // Check both total limit and type-specific limit
    if (!canAddButtonType(option) || isButtonLimitReached()) {
      return;
    }
    setSelectedOption(option);

    // Create new button with unique ID and type-specific data structure
    let newButton = {
      id: Date.now(),
      type: option,
      data: {},
    };

    // Initialize button data based on type
    switch (option) {
      case "marketing-opt-out":
        newButton.data = {
          buttonText: "Stop promotions",
          footerText: "Not interested? Tap on Stop promotions.",
        };
        break;
      case "custom":
        newButton.data = {
          buttonText: "",
        };
        break;
      case "visit-website":
        newButton.data = {
          buttonText: "",
          urlType: "static",
          url: "",
          sampleUrl: "",
        };
        break;
      case "call-phone-number":
        newButton.data = {
          buttonText: "",
          countryCode: "",
          phoneNumber: "",
        };
        break;
      case "whatsapp-form":
        newButton.data = {
          buttonText: "",
          formId: "",
          formName: "",
          prefill: {}, // {form field name: contact field}
          thankYouMessage: "",
        };
        break;
    }

    // Add new button and update count
    setAllButtons([...allButtons, newButton]);
    setButtonCounts({
      ...buttonCounts,
      [option]: buttonCounts[option] + 1,
    });
    setSelectedOption("");
  };

  // Remove button and update type count
  const handleRemoveButton = (id, type) => {
    setAllButtons(allButtons.filter((button) => button.id !== id));
    setButtonCounts({
      ...buttonCounts,
      [type]: buttonCounts[type] - 1,
    });
    resetButtonStates(type);
  };

  // Centralized function to update button data
  const updateButtonData = (id, newData) => {
    setAllButtons(
      allButtons.map((button) =>
        button.id === id
          ? { ...button, data: { ...button.data, ...newData } }
          : button
      )
    );
  };

  // URL validation
  const isValidURL = (url) => {
    const urlRegex =
      /(https?:\/\/(?:www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b(?:[-a-zA-Z0-9()@:%_\+.~#?&//=]*))/g;
    return urlRegex.test(url);
  };

  const ButtonHeader = ({ allButtons }) => {
    return (
      <Grid item xs={12}>
        <Typography
          variant="h6"
          sx={{ ...typography.h1, mb: 1 }}
        >
          Buttons
          {allButtons.length === 0 && (
            <span style={{ opacity: 0.4 }}> (Optional)</span>
          )}
        </Typography>
        <Typography variant="body1" sx={{ ...typography.h2,  mb: 1, fontWeight: 500 }}>
          Create buttons that let customers respond to your message or take
          action.
        </Typography>
        <Typography variant="body2" sx={{ ...typography.bodyLarge, fontWeight: 500, fontStyle: "italic" }}>
          If you add more than three buttons, they will appear in a list · You
          can add upto <b>10 buttons</b> in a template.
        </Typography>
      </Grid>
    );
  };

  const handleReorder = (buttonId, newPosition) => {
    setAllButtons((prevButtons) => {
      const buttons = [...prevButtons];
      const currentIndex = buttons.findIndex(
        (button) => button.id === buttonId
      );
      const button = buttons[currentIndex];

      // Remove button from current position
      buttons.splice(currentIndex, 1);
      // Insert button at new position
      buttons.splice(newPosition, 0, button);

      return buttons;
    });
  };

  // Modular button rendering based on type
  const renderButton = (button) => {
    const orderSlider = (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Tooltip title="Drag to reorder">
          <IconButton
            sx={{
              cursor: "move",
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.04)",
              },
            }}
            onMouseDown={(e) => {
              const currentIndex = allButtons.findIndex(
                (b) => b.id === button.id
              );
              const containerRect =
                e.currentTarget.parentElement.parentElement.getBoundingClientRect();
              const buttonHeight = containerRect.height;

              const handleMouseMove = (moveEvent) => {
                const deltaY = moveEvent.clientY - e.clientY;
                const newPosition = Math.round(
                  currentIndex + deltaY / buttonHeight
                );
                if (newPosition >= 0 && newPosition < allButtons.length) {
                  handleReorder(button.id, newPosition);
                }
              };

              const handleMouseUp = () => {
                document.removeEventListener("mousemove", handleMouseMove);
                document.removeEventListener("mouseup", handleMouseUp);
              };

              document.addEventListener("mousemove", handleMouseMove);
              document.addEventListener("mouseup", handleMouseUp);
            }}
          >
            <DragIndicatorIcon sx={{ color: "grey.500" }} />
          </IconButton>
        </Tooltip>
      </Box>
    );

    const removeButton = (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "start",
        }}
      >
        <IconButton onClick={() => handleRemoveButton(button.id, button.type)}>
          <CloseIcon />
        </IconButton>
      </Box>
    );

    switch (button.type) {
      case "marketing-opt-out":
        setFooter(button.data.footerText);
        return (
          <>
            <Divider
              sx={{
                width: "100%",
                mt: 2,
              }}
            />

            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                gap: 1,
              }}
              key={button.id}
            >
              {orderSlider}

              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                  flex: 1,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    gap: 1,
                    flexWrap: {
                      xs: "wrap",
                      sm: "nowrap",
                    },
                  }}
                >
                  <TextField label="Type" value="marketing-opt-out" disabled />

                  <TextField
                    label="Button text"
                    value={button.data.buttonText}
                    disabled
                  />

                  <TextField
                    label="Footer text"
                    value={button.data.footerText}
                    disabled
                    fullWidth
                  />
                </Box>

                <Grid
                  item
                  xs={12}
                  sx={{
                    display: "flex", // Creates the flex container
                    alignItems: "flex-start", // Aligns checkbox to top of text (not center)
                    gap: 1, // Adds consistent space between checkbox and text
                  }}
                >
                  <Checkbox
                    checked={tAndCChecked}
                    onChange={(e) => setTAndCChecked(e.target.checked)}
                    sx={{
                      p: 0, // Optional: Removes default padding to align tighter to left
                      // Optional: Pushes checkbox down slightly to align with first line of text
                    }}
                  />
                  <Typography sx={{ ...typography.bodyLarge }} variant="body2" component="span">
                    I / We understand its not BotBridge's responsibility to
                    ensure Opted Out users are not re-targeted. I / We will
                    ensure that the Opted Out users are filtered out during
                    Segment creation process.
                  </Typography>
                </Grid>

                {!tAndCChecked && (
                  <Grid item xs={12}>
                    <Typography sx={{ ...typography.bodyLarge }} variant="body2" color="error">
                      This box must be ticked to add this button.
                    </Typography>
                  </Grid>
                )}
              </Box>

              {removeButton}
            </Box>
          </>
        );

      case "custom":
        return (
          <>
            <Divider
              sx={{
                width: "100%",
                mt: 2,
              }}
            />

            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                gap: 1,
              }}
              key={button.id}
            >
              {orderSlider}

              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  flex: 1,
                  gap: 1,
                  flexWrap: {
                    xs: "wrap",
                    sm: "nowrap",
                  },
                }}
              >
                <TextField label="Type" value="custom" disabled />

                <TextField
                  label="Button text"
                  value={button.data.buttonText}
                  onChange={(e) => {
                    if (
                      e.target.value === "" ||
                      e.target.value.trim().length > 0
                    ) {
                      if (e.target.value.length <= 25) {
                        updateButtonData(button.id, {
                          buttonText: e.target.value,
                        });
                      }
                    }
                  }}
                  InputProps={{
                    endAdornment: (
                      <Typography
                        variant="body2"
                        sx={{
                          color: "gray",
                          position: "absolute",
                          right: "10px",
                          top: "12px",
                        }}
                      >
                        {`${button.data.buttonText.length}/25`}
                      </Typography>
                    ),
                  }}
                  fullWidth
                  required
                />
              </Box>

              {removeButton}
            </Box>
          </>
        );

      case "whatsapp-form": {
        const pickedForm =
          publishedForms.find((f) => f.id === button.data.formId) || null;
        const fields = formFields[button.data.formId];
        // Screens that have prefillable fields, in form order.
        const prefillScreens = [
          ...new Map((fields || []).map((f) => [f.screen.id, f.screen])).values(),
        ];
        const screenId = prefillScreens.some((s) => s.id === prefillScreen[button.id])
          ? prefillScreen[button.id]
          : prefillScreens[0]?.id;
        const mappedCount = (id) =>
          fields.filter((f) => f.screen.id === id && button.data.prefill?.[f.name]).length;
        return (
          <>
            <Divider sx={{ width: "100%", mt: 2 }} />
            <Box sx={{ display: "flex", flexDirection: "row", gap: 1 }} key={button.id}>
              {orderSlider}
              <Stack spacing={2} sx={{ flex: 1 }}>
                <Box
                  sx={{
                    display: "flex",
                    gap: 1,
                    flexWrap: { xs: "wrap", sm: "nowrap" },
                  }}
                >
                  <TextField label="Type" value="WhatsApp Form" disabled />
                  <TextField
                    label="Button text"
                    value={button.data.buttonText}
                    onChange={(e) => {
                      if (e.target.value.length <= 25) {
                        updateButtonData(button.id, { buttonText: e.target.value });
                      }
                    }}
                    InputProps={{
                      endAdornment: (
                        <Typography
                          variant="body2"
                          sx={{ color: "gray", position: "absolute", right: "10px", top: "12px" }}
                        >
                          {`${button.data.buttonText.length}/25`}
                        </Typography>
                      ),
                    }}
                    fullWidth
                    required
                  />
                </Box>
                <Autocomplete
                  options={publishedForms}
                  value={pickedForm}
                  getOptionLabel={(f) => f?.name || ""}
                  isOptionEqualToValue={(a, b) => a.id === b.id}
                  noOptionsText="No published forms"
                  onChange={(e, form) => {
                    updateButtonData(button.id, {
                      formId: form?.id || "",
                      formName: form?.name || "",
                      prefill: {},
                    });
                    if (form) loadFormFields(form.id);
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label="Form (published)" required />
                  )}
                />
                {button.data.formId && fields && (
                  <Box>
                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                      <Typography sx={{ ...typography.bodyLarge, fontWeight: 500 }}>
                        Prefill form fields
                      </Typography>
                      <Typography sx={{ ...typography.bodySmall, color: "gray" }}>
                        Filled in when the form opens
                      </Typography>
                    </Box>
                    {fields.length === 0 && (
                      <Typography sx={{ ...typography.bodySmall, color: "gray" }}>
                        This form has no text fields that can be prefilled (fields under
                        a "Show only when" rule can't be). Forms published before prefill
                        was added need to be published again from the form builder.
                      </Typography>
                    )}
                    {prefillScreens.length > 0 && (
                      <FormControl size="small" fullWidth sx={{ mb: 1 }}>
                        <InputLabel id={`prefill-screen-${button.id}`}>Screen</InputLabel>
                        <Select
                          labelId={`prefill-screen-${button.id}`}
                          label="Screen"
                          value={screenId}
                          onChange={(e) =>
                            setPrefillScreen((prev) => ({ ...prev, [button.id]: e.target.value }))
                          }
                        >
                          {prefillScreens.map((s) => {
                            const n = mappedCount(s.id);
                            return (
                              <MenuItem key={s.id} value={s.id}>
                                {s.title}
                                {n > 0 ? ` · ${n} prefilled` : ""}
                              </MenuItem>
                            );
                          })}
                        </Select>
                      </FormControl>
                    )}
                    {fields.filter((f) => f.screen.id === screenId).map((f) => {
                      // Source: a contact field ("name"), a body variable ("var:1")
                      // or a fixed text ("custom:..."); see backend prefill_value.
                      const source = button.data.prefill?.[f.name] || "";
                      const isCustom = source.startsWith("custom:");
                      const setSource = (value) =>
                        updateButtonData(button.id, {
                          prefill: { ...button.data.prefill, [f.name]: value || undefined },
                        });
                      // Keep a variable picked earlier listed even if the body lost it.
                      const variables = source.startsWith("var:") && !bodyVariables.includes(source.slice(4))
                        ? [...bodyVariables, source.slice(4)]
                        : bodyVariables;
                      return (
                        <Box
                          key={f.name}
                          sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 2,
                            py: 1,
                            borderTop: "1px solid #EEF0F3",
                          }}
                        >
                          <Box sx={{ flex: 1, minWidth: 0, pt: 0.5 }}>
                            <Typography sx={typography.bodyLarge}>{f.label}</Typography>
                            <Typography sx={{ ...typography.bodySmall, color: "gray" }}>
                              {f.name}
                            </Typography>
                          </Box>
                          <Stack spacing={1} sx={{ flex: 1 }}>
                            <FormControl size="small" fullWidth>
                              <Select
                                value={isCustom ? "custom" : source}
                                displayEmpty
                                onChange={(e) =>
                                  setSource(e.target.value === "custom" ? "custom:" : e.target.value)
                                }
                              >
                                {[
                                  <MenuItem key="none" value="">Leave empty</MenuItem>,
                                  <ListSubheader key="h-contact">Contact</ListSubheader>,
                                  ...CONTACT_FIELDS.map((c) => (
                                    <MenuItem key={c} value={c}>
                                      Contact: {c}
                                    </MenuItem>
                                  )),
                                  <ListSubheader key="h-var">Template variable</ListSubheader>,
                                  ...(variables.length
                                    ? variables.map((n) => (
                                        <MenuItem key={`var:${n}`} value={`var:${n}`}>
                                          {`Variable {{${n}}}`}
                                          {bodyVariables.includes(n) ? "" : " (not in the body)"}
                                        </MenuItem>
                                      ))
                                    : [
                                        <MenuItem key="var-none" disabled>
                                          Add {"{{1}}"} to the body to use it here
                                        </MenuItem>,
                                      ]),
                                  <ListSubheader key="h-custom">Custom</ListSubheader>,
                                  <MenuItem key="custom" value="custom">
                                    Custom value
                                  </MenuItem>,
                                ]}
                              </Select>
                            </FormControl>
                            {isCustom && (
                              <TextField
                                size="small"
                                fullWidth
                                placeholder="Value to fill in"
                                value={source.slice("custom:".length)}
                                onChange={(e) => setSource(`custom:${e.target.value}`)}
                                inputProps={{ maxLength: 80 }}
                                helperText="Same value for every recipient"
                              />
                            )}
                            {source.startsWith("var:") && (
                              <Typography sx={{ ...typography.bodySmall, color: "gray" }}>
                                Each recipient's value of {`{{${source.slice(4)}}}`} in the message
                              </Typography>
                            )}
                          </Stack>
                        </Box>
                      );
                    })}
                  </Box>
                )}
                <TextField
                  label="Thank-you message (optional)"
                  placeholder="Thank you! We have received your response."
                  value={button.data.thankYouMessage}
                  onChange={(e) =>
                    updateButtonData(button.id, { thankYouMessage: e.target.value })
                  }
                  fullWidth
                />
                <Typography variant="body2" sx={{ ...typography.bodySmall, color: "gray" }}>
                  When a customer taps this button, the form opens straight away.
                </Typography>
              </Stack>
              {removeButton}
            </Box>
          </>
        );
      }

      case "visit-website":
        return (
          <>
            <Divider
              sx={{
                width: "100%",
                mt: 2,
              }}
            />

            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                gap: 1,
              }}
              key={button.id}
            >
              {orderSlider}

              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  flex: 1,
                  gap: 1,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "flex-start",
                    gap: 1,
                    flexWrap: {
                      xs: "wrap",
                      sm: "nowrap",
                    },
                  }}
                >
                  <TextField label="Type" value="Visit Website" disabled />

                  <TextField
                    label="Button Text"
                    value={button.data.buttonText}
                    onChange={(e) => {
                      if (
                        e.target.value === "" ||
                        e.target.value.trim().length > 0
                      ) {
                        if (e.target.value.length <= 25) {
                          updateButtonData(button.id, {
                            buttonText: e.target.value,
                          });
                        }
                      }
                    }}
                    InputProps={{
                      sx: {
                        "& input": {
                          paddingRight: "10px", // <-- space for the counter
                        },
                      },
                      endAdornment: (
                        <Typography
                          variant="body2"
                          sx={{
                            color: "gray",
                            position: "absolute",
                            right: "10px",
                            top: "12px",
                          }}
                        >
                          {`${button.data.buttonText.length}/25`}
                        </Typography>
                      ),
                    }}
                    required
                  />

                  <Select
                    value={button.data.urlType}
                    onChange={(e) => {
                      updateButtonData(button.id, { urlType: e.target.value });
                    }}
                  >
                    <MenuItem value="static">Static</MenuItem>
                    <MenuItem value="dynamic">Dynamic</MenuItem>
                  </Select>

                  <TextField
                    label="URL"
                    value={button.data.url}
                    placeholder={
                      button.data.urlType === "static"
                        ? "https://www.google.com"
                        : "https://www.google.com/search?q={{1}}"
                    }
                    onChange={(e) => {
                      if (
                        e.target.value === "" ||
                        e.target.value.trim().length > 0
                      ) {
                        if (e.target.value.length <= 2000) {
                          updateButtonData(button.id, { url: e.target.value });
                        }
                      }
                    }}
                    InputProps={{
                      sx: {
                        "& input": {
                          paddingRight: "10px", // <-- space for the counter
                        },
                      },
                      endAdornment: (
                        <Typography
                          variant="body2"
                          sx={{
                            color: "gray",
                            position: "absolute",
                            right: "10px",
                            top: "12px",
                          }}
                        >
                          {`${button.data.url.length}/2000`}
                        </Typography>
                      ),
                    }}
                    error={!isValidURL(button.data.url)}
                    helperText={
                      !isValidURL(button.data.url) ? "Invalid URL" : ""
                    }
                    // fullWidth
                    sx={{ flex: 1 }}
                    required
                  />
                </Box>

                {button.data.urlType === "dynamic" &&
                  hasDynamicPlaceholder(button.data.url) && (
                    <Box sx={{ display: "flex", flexDirection: "row", gap: 1 }}>
                      <Typography variant="body1">{"{{1}}:"}</Typography>
                      <TextField
                        sx={{ flex: 1 }}
                        value={button.data.sampleUrl}
                        onChange={(e) => {
                          if (
                            e.target.value === "" ||
                            e.target.value.trim().length > 0
                          ) {
                            if (e.target.value.length <= 2000) {
                              updateButtonData(button.id, {
                                sampleUrl: e.target.value,
                              });
                            }
                          }
                        }}
                        InputProps={{
                          endAdornment: (
                            <Typography
                              variant="body2"
                              sx={{
                                color: "gray",
                                position: "absolute",
                                right: "10px",
                                top: "12px",
                              }}
                            >
                              {`${button.data.sampleUrl.length}/2000`}
                            </Typography>
                          ),
                        }}
                        fullWidth={false}
                        required
                      />
                    </Box>
                  )}
              </Box>

              {removeButton}
            </Box>
            <Typography variant="body2" sx={{ fontStyle: "italic" }}>
              {button.data.urlType === "static" &&
                "The entire URL is fixed and cannot change · No parameters are allowed."}

              {button.data.urlType === "dynamic" &&
                "The URL contains a fixed prefix followed by a dynamic value ({{1}}). It can have a static beginning and a dynamic ending."}
            </Typography>
          </>
        );

      case "call-phone-number":
        return (
          <>
            <Divider
              sx={{
                width: "100%",
                mt: 2,
              }}
            />

            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                gap: 1,
              }}
              key={button.id}
            >
              {orderSlider}

              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  flex: 1,
                  gap: 1,
                }}
              >
                {/* Phone button fields */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    gap: 1,
                    width: "100%",
                    flexWrap: {
                      xs: "wrap",
                      sm: "nowrap",
                    },
                  }}
                >
                  <TextField
                    label="Type of function"
                    value="Call Phone Number"
                    disabled
                  />
                  <TextField
                    label="Button Text"
                    value={button.data.buttonText}
                    onChange={(e) => {
                      if (
                        e.target.value === "" ||
                        e.target.value.trim().length > 0
                      ) {
                        if (e.target.value.length <= 25) {
                          updateButtonData(button.id, {
                            buttonText: e.target.value,
                          });
                        }
                      }
                    }}
                    InputProps={{
                      endAdornment: (
                        <Typography
                          variant="body2"
                          sx={{
                            color: "gray",
                            position: "absolute",
                            right: "10px",
                            top: "12px",
                          }}
                        >
                          {`${button.data.buttonText.length}/25`}
                        </Typography>
                      ),
                    }}
                    fullWidth
                    required
                    sx={{
                      flex: 1,
                    }}
                  />
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                  }}
                >
                  <Checkbox
                    checked={tollFreeNumber}
                    onChange={(e) => {
                      setTollFreeNumber(e.target.checked);
                      if (e.target.checked) {
                        updateButtonData(button.id, { countryCode: "" });
                      }
                    }}
                    color="primary"
                    inputProps={{ "aria-label": "is-dynamic-checkbox" }}
                  />
                  <Typography variant="body2">Toll Free Number</Typography>
                </Box>

                <Box
                  key={`phone-grid-${tollFreeNumber}`}
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    gap: 1,
                    width: "100%",
                    flexWrap: {
                      xs: "wrap",
                      sm: "nowrap",
                    },
                  }}
                >
                  {!tollFreeNumber && (
                    <CallingCodeSelect
                      value={button.data.countryCode}
                      onChange={(code) =>
                        updateButtonData(button.id, { countryCode: code })
                      }
                      required
                      sx={{ minWidth: 200, flexShrink: 0 }}
                      TextFieldProps={{ placeholder: "Search country, ISO or code" }}
                    />
                  )}

                  <TextField
                    label={
                      tollFreeNumber
                        ? "Phone Number (1800XXXXXXX)"
                        : "Phone Number"
                    }
                    value={button.data.phoneNumber}
                    onChange={(e) => {
                      const input = e.target.value;
                      if (input === "" || input.trim().length > 0) {
                        if (/^\d*$/.test(input) && input.length <= 20) {
                          updateButtonData(button.id, { phoneNumber: input });
                        }
                      }
                    }}
                    InputProps={{
                      endAdornment: (
                        <Typography
                          variant="body2"
                          sx={{
                            color: "gray",
                            position: "absolute",
                            right: "10px",
                            top: "12px",
                          }}
                        >
                          {`${button.data.phoneNumber.length}/20`}
                        </Typography>
                      ),
                    }}
                    fullWidth
                    required
                    sx={{
                      flex: 1,
                    }}
                  />
                </Box>
              </Box>

              {removeButton}
            </Box>
          </>
        );
    }
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Stack container spacing={2} sx={{ width: "100%" }}>
        <ButtonHeader allButtons={allButtons} />

        <Grid item xs={12}>
          <Select
            sx={{ width: "auto" }}
            value={selectedOption}
            onChange={handleOptionChange}
            displayEmpty
            disabled={isButtonLimitReached()}
            size="small"
          >
            <MenuItem value="" disabled>
              + Add a button
            </MenuItem>

            {/* <Divider
              sx={{
                width: "100%",
                borderColor: "gray",
                borderWidth: 1,
                opacity: 0.2,
                mt: 2,
              }}
            /> */}

            <ListSubheader>Quick reply button</ListSubheader>
            {templateType === "marketing-template" && (
              <MenuItem
                value="marketing-opt-out"
                disabled={!canAddButtonType("marketing-opt-out")}
              >
                Marketing opt-out{" "}
                <span style={{ opacity: 0.6, marginLeft: "5px" }}>
                  ({buttonCounts["marketing-opt-out"]}/1)
                </span>
              </MenuItem>
            )}
            <MenuItem value="custom" disabled={!canAddButtonType("custom")}>
              Custom{" "}
              <span style={{ opacity: 0.6, marginLeft: "5px" }}>
                ({buttonCounts["custom"]}/10)
              </span>
            </MenuItem>
            <MenuItem
              value="whatsapp-form"
              disabled={!canAddButtonType("whatsapp-form")}
            >
              WhatsApp Form{" "}
              <span style={{ opacity: 0.6, marginLeft: "5px" }}>
                ({buttonCounts["whatsapp-form"]}/1)
              </span>
            </MenuItem>

            <Divider
              sx={{
                width: "100%",
                borderColor: "gray",
                borderWidth: 1,
                opacity: 0.2,
                mt: 2,
              }}
            />

            <ListSubheader>Call-to-action buttons</ListSubheader>
            <MenuItem
              value="visit-website"
              disabled={!canAddButtonType("visit-website")}
            >
              Visit Website{" "}
              <span style={{ opacity: 0.6, marginLeft: "5px" }}>
                ({buttonCounts["visit-website"]}/2)
              </span>
            </MenuItem>
            <MenuItem
              value="call-phone-number"
              disabled={!canAddButtonType("call-phone-number")}
            >
              Call Phone Number{" "}
              <span style={{ opacity: 0.6, marginLeft: "5px" }}>
                ({buttonCounts["call-phone-number"]}/1)
              </span>
            </MenuItem>
          </Select>
        </Grid>

        {/* Render all buttons using the modular rendering function */}
        {allButtons.map((button) => renderButton(button))}

        {buttonsMixed && (
          <Typography sx={{ ...typography.bodyLarge }} variant="body2" color="error">
            Quick reply buttons (Custom / Marketing opt-out) and call-to-action
            buttons (Visit Website / Call Phone Number) must be grouped together,
            not mixed. Reorder them so all quick reply buttons come before or
            after the others.
          </Typography>
        )}
      </Stack>
    </Box>
  );
};

export default ButtonComponent;
